function checkForStockReportRateRequirements (pgConnection, programs) {
  if (!pgConnection) {
    const err = new Error('Postgres connection is required')
    err.status = 500
    throw err
  }

  if (!programs || (Array.isArray(programs) && programs.length === 0)) {
    const err = new Error('programs is required')
    err.status = 400
    throw err
  }
}

function buildStockReportRateQueryContext ({ programs, fromDateUtc, toDateExclusiveUtc, country, zone, state, lga, sdp }) {
  const normalizedPrograms = programs
  const params = [
    normalizedPrograms,
    fromDateUtc.toISOString(),
    toDateExclusiveUtc.toISOString()
  ]
  const whereClauses = []

  const scopeFilters = [
    ['country', country],
    ['zone', zone],
    ['state', state],
    ['lga', lga],
    ['sdp', sdp]
  ]

  scopeFilters.forEach(([column, value]) => {
    if (!value) {
      return
    }
    whereClauses.push(`AND srr.${column} = $${params.length + 1}`)
    params.push(value)
  })

  return {
    params,
    whereClauses
  }
}

function buildStockReportRateSummaryQuery ({ whereClauses }) {
  return `
      WITH filtered_obligations AS (
        SELECT
          srr.location_fsid,
          srr.program_fsid,
          srr.service_fsid,
          srr.submitted
        FROM analytics.stock_report_rate srr
        WHERE srr.program_fsid = ANY($1::text[])
          AND srr.period_start >= $2::timestamptz
          AND srr.period_start < $3::timestamptz
          ${whereClauses.join('\n')}
      ),
      completed_location_ids AS (
        SELECT
          fo.location_fsid
        FROM filtered_obligations fo
        GROUP BY fo.location_fsid
        HAVING bool_and(fo.submitted = true)
      )
      SELECT
        (SELECT count(DISTINCT location_fsid)::int FROM filtered_obligations) AS expected_locations,
        (SELECT count(*)::int FROM completed_location_ids) AS completed_locations
    `
}

function buildStockReportRateLocationTableByAreaQuery ({ whereClauses, groupedByColumn }) {
  return `
      WITH filtered_obligations AS (
        SELECT
          srr.location_fsid,
          srr.zone,
          srr.state,
          srr.lga,
          srr.sdp,
          srr.submitted
        FROM analytics.stock_report_rate srr
        WHERE srr.program_fsid = ANY($1::text[])
          AND srr.period_start >= $2::timestamptz
          AND srr.period_start < $3::timestamptz
          ${whereClauses.join('\n')}
      ),
      location_completion AS (
        SELECT
          fo.location_fsid,
          fo.zone,
          fo.state,
          fo.lga,
          fo.sdp,
          bool_and(fo.submitted = true) AS completed
        FROM filtered_obligations fo
        GROUP BY fo.location_fsid, fo.zone, fo.state, fo.lga, fo.sdp
      )
      SELECT
        lc.${groupedByColumn} AS grouped_by_value,
        count(*)::int AS total_number_of_locations_expected_to_report,
        count(*) FILTER (WHERE lc.completed = true)::int AS number_of_locations_completed
      FROM location_completion lc
      WHERE lc.${groupedByColumn} IS NOT NULL
      GROUP BY lc.${groupedByColumn}
      ORDER BY lc.${groupedByColumn} ASC
    `
}

function buildStockReportRateLocationTableBySdpQuery ({ whereClauses }) {
  return `
      WITH filtered_obligations AS (
        SELECT
          srr.location_fsid,
          srr.sdp,
          -- this is not used in the query, but is needed for the result mapping
          -- so that we can return the stock report fsids for the expected and submitted stock reports
          srr.stock_report_fsid,
          -- end of comment
          srr.submitted
        FROM analytics.stock_report_rate srr
        WHERE srr.program_fsid = ANY($1::text[])
          AND srr.period_start >= $2::timestamptz
          AND srr.period_start < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        fo.location_fsid,
        fo.sdp,
        bool_and(fo.submitted = true) AS completed,
        count(*)::int AS number_of_reports_expected,
        count(*) FILTER (WHERE fo.submitted = true)::int AS number_of_reports_submitted,
        -- this is not used in the query, but is needed for the result mapping
        -- so that we can return the stock report fsids for the expected and submitted stock reports
        -- this functionality has been added to improve debugging of the indicator in the UI
        -- but adds to the loading time. It can be removed if needed
        COALESCE(
          array_agg(fo.stock_report_fsid) FILTER (
            WHERE fo.stock_report_fsid IS NOT NULL
          ),
          ARRAY[]::text[]
        ) AS expected_stock_report_ids,
        COALESCE(
          array_agg(fo.stock_report_fsid) FILTER (
            WHERE fo.submitted = true AND fo.stock_report_fsid IS NOT NULL
          ),
          ARRAY[]::text[]
        ) AS submitted_stock_report_ids
      -- end of comment
      FROM filtered_obligations fo
      GROUP BY fo.location_fsid, fo.sdp
      ORDER BY fo.sdp ASC, fo.location_fsid ASC
    `
}

function buildStockReportRateLocationTableQuery ({ whereClauses, groupedBy }) {
  // At the SDP level, we show different data than at the other levels (lga, state, zone, national).
  // When grouped by sdp, we show the number of expected and submitted stock reports for each sdp
  // and the stock report fsids for the expected and submitted stock reports.
  // When grouped by other levels, we show the number of locations expected to report and the number of locations completed
  // without telling exactly which reports were expected and submitted, the only focus is completion.
  // For this reason, we have separate queries for the two cases.
  if (groupedBy === 'sdp') {
    return buildStockReportRateLocationTableBySdpQuery({ whereClauses })
  }

  const groupedByColumn = {
    zone: 'zone',
    state: 'state',
    lga: 'lga'
  }[groupedBy]
  if (!groupedByColumn) {
    const err = new Error('groupedBy must be one of zone, state, lga, sdp')
    err.status = 400
    throw err
  }
  return buildStockReportRateLocationTableByAreaQuery({ whereClauses, groupedByColumn })
}

function buildStockReportRateCycleByCycleQuery ({ whereClauses, limit }) {
  return `
      WITH filtered_obligations AS (
        SELECT
          srr.period_start,
          srr.location_fsid,
          srr.submitted
        FROM analytics.stock_report_rate srr
        WHERE srr.program_fsid = ANY($1::text[])
          AND srr.period_start >= $2::timestamptz
          AND srr.period_start < $3::timestamptz
          ${whereClauses.join('\n')}
      ),
      location_completion AS (
        SELECT
          fo.period_start,
          fo.location_fsid,
          bool_and(fo.submitted = true) AS completed
        FROM filtered_obligations fo
        GROUP BY fo.period_start, fo.location_fsid
      ),
      cycle_summary AS (
        SELECT
          lc.period_start,
          count(*)::int AS expected_locations,
          count(*) FILTER (WHERE lc.completed = true)::int AS completed_locations
        FROM location_completion lc
        GROUP BY lc.period_start
      )
      SELECT
        cs.period_start::date::text AS period_start_date,
        cs.expected_locations,
        cs.completed_locations
      FROM cycle_summary cs
      ORDER BY cs.period_start DESC
      LIMIT ${Number(limit) || 60}
    `
}

function buildStockReportRateProgramCyclesQuery () {
  return `
      SELECT
        dp.fsid,
        dp.options
      FROM avocado.data_program dp
      WHERE dp.fsid = ANY($1::text[])
    `
}

// These values match should match those in getLevel in backend/stacks/internal_api/endpoint/src/transformers.js
const STOCK_REPORT_RATE_THRESHOLDS = {
  ok: 0.9,
  warning: 0.7
}

function getStockReportRateLevel (value) {
  if (value === null || Number.isNaN(value)) {
    return 'noData'
  }
  if (value > STOCK_REPORT_RATE_THRESHOLDS.ok) {
    return 'ok'
  }
  if (value > STOCK_REPORT_RATE_THRESHOLDS.warning) {
    return 'warning'
  }
  return 'alert'
}

module.exports = {
  checkForStockReportRateRequirements,
  buildStockReportRateQueryContext,
  buildStockReportRateSummaryQuery,
  buildStockReportRateLocationTableQuery,
  buildStockReportRateCycleByCycleQuery,
  buildStockReportRateProgramCyclesQuery,
  getStockReportRateLevel
}
